import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  TextField,
  IconButton,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Grid,
  Button,
  alpha,
  useTheme,
  Avatar,
} from '@mui/material';
import {
  Send,
  SmartToy,
  Person,
  Lightbulb,
} from '@mui/icons-material';
import { api } from '../../services/api';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  agent_used?: string;
  suggestions?: string[];
}

const ChatPage: React.FC = () => {
  const theme = useTheme();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load suggestions on mount
  useEffect(() => {
    loadSuggestions();
  }, []);

  const loadSuggestions = async () => {
    try {
      const response = await api.chat.getSuggestions();
      // Ensure suggestions is an array and has the expected structure
      const suggestionsData = response.data.suggestions || [];
      setSuggestions(Array.isArray(suggestionsData) ? suggestionsData : []);
    } catch (error) {
      console.error('Error loading suggestions:', error);
      setSuggestions([]);
    }
  };

  const sendMessage = async (messageText?: string) => {
    const textToSend = messageText || input.trim();
    if (!textToSend) return;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: textToSend,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await api.chat.sendMessage(textToSend);
      const data = response.data;

      // Add assistant message
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.response,
        timestamp: new Date(),
        agent_used: data.agent_used,
        suggestions: data.suggestions,
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error: any) {
      console.error('Error sending message:', error);
      
      // Add error message
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your message. Please try again.',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const handleSuggestionClick = (question: string) => {
    sendMessage(question);
  };

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          AI Assistant
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Ask me anything about your supply chain
        </Typography>
      </Box>

      {/* Chat Container */}
      <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Messages */}
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
          {messages.length === 0 ? (
            // Welcome Screen
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <SmartToy sx={{ fontSize: 80, color: theme.palette.primary.main, mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                Welcome to AI Assistant
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                I can help you with supply chain analysis, forecasting, optimization, and more.
              </Typography>

              {/* Suggested Questions */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Try asking:
                </Typography>
                <Grid container spacing={2} sx={{ maxWidth: 800, mx: 'auto' }}>
                  {suggestions.map((category, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Paper
                        sx={{
                          p: 2,
                          textAlign: 'left',
                          border: 1,
                          borderColor: 'divider',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            bgcolor: alpha(theme.palette.primary.main, 0.02),
                          },
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          {category.category}
                        </Typography>
                        {(category.questions || []).slice(0, 2).map((q: string, qIdx: number) => (
                          <Button
                            key={qIdx}
                            fullWidth
                            size="small"
                            sx={{ justifyContent: 'flex-start', mb: 0.5, textAlign: 'left' }}
                            onClick={() => handleSuggestionClick(q)}
                          >
                            <Lightbulb sx={{ fontSize: 16, mr: 1 }} />
                            <Typography variant="caption" sx={{ textTransform: 'none' }}>
                              {q}
                            </Typography>
                          </Button>
                        ))}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          ) : (
            // Messages
            <Box>
              {messages.map((message) => (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    mb: 3,
                    justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                  }}
                >
                  {message.role === 'assistant' && (
                    <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                      <SmartToy />
                    </Avatar>
                  )}
                  
                  <Box sx={{ maxWidth: '70%' }}>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: message.role === 'user'
                          ? theme.palette.primary.main
                          : alpha(theme.palette.grey[100], 0.8),
                        color: message.role === 'user' ? 'white' : 'text.primary',
                      }}
                    >
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {message.content}
                      </Typography>
                      
                      {message.agent_used && (
                        <Chip
                          label={message.agent_used.replace('_', ' ')}
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Paper>

                    {/* Suggestions */}
                    {message.suggestions && message.suggestions.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          Suggested follow-ups:
                        </Typography>
                        {message.suggestions.map((suggestion, idx) => (
                          <Chip
                            key={idx}
                            label={suggestion}
                            size="small"
                            onClick={() => handleSuggestionClick(suggestion)}
                            sx={{ mr: 0.5, mb: 0.5, cursor: 'pointer' }}
                          />
                        ))}
                      </Box>
                    )}

                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {message.timestamp.toLocaleTimeString()}
                    </Typography>
                  </Box>

                  {message.role === 'user' && (
                    <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                      <Person />
                    </Avatar>
                  )}
                </Box>
              ))}

              {loading && (
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                    <SmartToy />
                  </Avatar>
                  <Paper sx={{ p: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" sx={{ ml: 2, display: 'inline' }}>
                      Thinking...
                    </Typography>
                  </Paper>
                </Box>
              )}

              <div ref={messagesEndRef} />
            </Box>
          )}
        </Box>

        {/* Input */}
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Ask me anything about your supply chain..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
            />
            <IconButton
              color="primary"
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              sx={{
                bgcolor: theme.palette.primary.main,
                color: 'white',
                '&:hover': {
                  bgcolor: theme.palette.primary.dark,
                },
                '&:disabled': {
                  bgcolor: theme.palette.action.disabledBackground,
                },
              }}
            >
              <Send />
            </IconButton>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default ChatPage;
